import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService, Translation } from '@ngx-translate/core';
import { AccordionItem } from '../../models/accordion-item.model';
import { PlatformMode } from '../../models/platform-mode.model';
import { PlatformModeService } from '../../services/platform-mode.service';
import { PrivacyContentComponent } from './privacy-content/privacy-content.component';

@Component({
  selector: 'flex-privacy-policy-policies',
  standalone: true,
  imports: [TranslateModule, PrivacyContentComponent],
  templateUrl: './policies.component.html',
  styleUrl: './policies.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PoliciesComponent {
  private readonly translateService = inject(TranslateService);
  private readonly platformModeService = inject(PlatformModeService);
  private readonly langChangeSignal = toSignal(this.translateService.onLangChange);
  readonly accordionItems = signal<AccordionItem[]>([]);
  readonly mode: PlatformMode = this.platformModeService.getMode();
  readonly translationPrefix = `PRIVACY_POLICY.POLICIES.${this.mode}`;
  constructor() {
    effect(() => {
      this.langChangeSignal();
      this.onSwitchLang();
    });

    this.onSwitchLang();
  }

  private onSwitchLang() {
    this.accordionItems.set([
      {
        title: this.translate('DATA_CONTROLLER.TITLE'),
        icon: 'corporate_fare',
        content: this.translate('DATA_CONTROLLER.CONTENT'),
      },
      {
        title: this.translate('DPO.TITLE'),
        icon: 'shield',
        content: this.translate('DPO.CONTENT'),
      },
      {
        title: this.translate('DATA_PROCESSED.TITLE'),
        icon: 'badge',
        content: this.translate('DATA_PROCESSED.CONTENT'),
      },
      {
        title: this.translate('PURPOSES.TITLE'),
        icon: 'task',
        content: this.buildPurposeTableHtml(),
      },
      {
        title: this.translate('RECIPIENTS.TITLE'),
        icon: 'group',
        content: this.translate('RECIPIENTS.CONTENT'),
      },
      {
        title: this.translate('TRANSFERS.TITLE'),
        icon: 'public',
        content: this.translate('TRANSFERS.CONTENT'),
      },
      {
        title: this.translate('RETENTION.TITLE'),
        icon: 'schedule',
        content: this.translate('RETENTION.CONTENT'),
      },
      {
        title: this.translate('RIGHTS.TITLE'),
        icon: 'groups',
        content: this.translate('RIGHTS.CONTENT'),
      },
    ]);
  }

  private translate(key: string): Translation {
    return this.translateService.instant(`${this.translationPrefix}.${key}`);
  }

  private buildPurposeTableHtml(): string {
    const intro = this.translate('PURPOSES.INTRO');
    const outro = this.translate('PURPOSES.OUTRO');
    const tr = this.translate('PURPOSES.TABLE');
    return `
    ${intro}
		<div class="privacy-table privacy-table--purposes">
			<div class="privacy-table__scroll">
				<table class="privacy-table__table">
				<thead class="privacy-table__head">
					<tr>
					<th>${tr.HEAD_PURPOSE}</th>
					<th>${tr.HEAD_LEGAL_BASIS}</th>
					</tr>
				</thead>
				<tbody class="privacy-table__body">
					<tr>
					<td>${tr.ROW_USE_APP_PURPOSE}</td>
					<td>${tr.ROW_USE_APP_BASIS}</td>
					</tr>
					<tr>
					<td>${tr.ROW_CHECK_FUNCTIONING_PURPOSE}</td>
					<td>${tr.ROW_CHECK_FUNCTIONING_BASIS}</td>
					</tr>
					<tr>
					<td>${tr.ROW_SECURITY_PURPOSE}</td>
					<td>${tr.ROW_SECURITY_BASIS}</td>
					</tr>
				</tbody>
				</table>
			</div>
		</div>
    ${outro}
  `;
  }
}
